import { useCallback, useEffect, useRef, useState } from 'react';
import { stableSignedUrl } from '../utils/signedUrlCache';

// Define interfaces directly or import if they are moved/exported elsewhere
interface MediaItem {
	_id: string;
	path: string;
	type: 'image' | 'video';
	thumbnail_path: string;
	compressed_path?: string;
	original_file_size?: number;
	file_size: number;
	width: number;
	height: number;
	rotation?: number;
	thumbnail_width: number;
	thumbnail_height: number;
	upload_date: string;
	album: string;
}

interface MediaList {
	mediaItems: MediaItem[];
	pageCount: number;
}

interface GalleryImage {
	_id?: string; // Optional _id for comparison
	src: string;
	width: number;
	height: number;
	rotation?: number;
	caption?: string;
}

interface UseLightboxProps {
	pictureList: MediaList | null | undefined;
	page: number;
	handlePageChange: (newPage: number) => void;
}

interface UseLightboxReturn {
	isLightboxOpen: boolean;
	currentImage: number | null;
	fullImages: GalleryImage[];
	openLightbox: (index: number) => void;
	closeLightbox: () => void;
	changeImage: (newIndex: number) => void;
}

// Preload an image URL into the browser cache so it renders instantly when used in an <img> tag
const preloadBrowserImage = (url: string) => {
	const img = new Image();
	img.src = url;
};

const useLightbox = ({
	pictureList,
	page,
	handlePageChange,
}: UseLightboxProps): UseLightboxReturn => {
	const [fullImages, setFullImages] = useState<GalleryImage[]>([]);
	const fullImagesRef = useRef<GalleryImage[]>(fullImages);
	const [currentImage, setCurrentImage] = useState<number | null>(null);
	const [targetIndexAfterPageLoad, setTargetIndexAfterPageLoad] = useState<
		number | null
	>(null);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);

	// --- Logic moved from Pictures.tsx ---

	const loadFullImage = useCallback(
		(index: number) => {
			if (!pictureList || !pictureList.mediaItems[index]) return;

			const image = pictureList.mediaItems[index];

			// Check if already loaded using the ref to avoid stale state issues
			if (fullImagesRef.current[index]) {
				// console.log('Already there (ref check), index:', index);
				return;
			}

			// Preload the image into the browser cache so it renders immediately
			// when the carousel switches to this slide, even during the animation
			// stableSignedUrl reuses the windowed signed URL so repeat views hit
			// the browser's HTTP cache instead of re-downloading (signedUrlCache.ts)
			const loadSrc = stableSignedUrl(image.compressed_path || image.path);
			preloadBrowserImage(loadSrc);

			const rot = image.rotation ?? 0;
			const isRotated90 = rot === 90 || rot === 270;
			const fullImage: GalleryImage = {
				_id: image._id, // Add the id
				src: loadSrc,
				// Effective (rotated) dimensions for layout
				width: isRotated90 ? image.height : image.width,
				height: isRotated90 ? image.width : image.height,
				rotation: rot,
			};

			setFullImages((prevImages) => {
				// Double check within state update to be safe
				if (prevImages[index]) {
					return prevImages;
				}
				const newImages = [...prevImages];
				newImages[index] = fullImage;
				// console.log('Loading full image for index:', index, newImages);
				return newImages;
			});
		},
		[pictureList] // Removed fullImages dependency here, rely on ref
	);

	const changeImage = useCallback(
		(newIndex: number) => {
			if (!pictureList) return;

			const totalImagesOnCurrentPage = pictureList.mediaItems.length;
			let finalIndex = newIndex;
			let targetPage = page;

			const isSinglePage = pictureList.pageCount <= 1;

			if (newIndex < 0) {
				if (isSinglePage) {
					finalIndex = totalImagesOnCurrentPage - 1;
					setCurrentImage(finalIndex);
					// Preload neighbours for single-page wrap
					if (!fullImagesRef.current[finalIndex])
						loadFullImage(finalIndex);
					if (
						finalIndex - 1 >= 0 &&
						!fullImagesRef.current[finalIndex - 1]
					)
						loadFullImage(finalIndex - 1);
					if (
						totalImagesOnCurrentPage > 1 &&
						!fullImagesRef.current[0]
					)
						loadFullImage(0);
				} else {
					targetPage =
						page === 1 ? pictureList.pageCount || 1 : page - 1;
					if (page !== targetPage) {
						handlePageChange(targetPage);
						setTargetIndexAfterPageLoad(-1); // Signal last image
					}
				}
			} else if (newIndex >= totalImagesOnCurrentPage) {
				if (isSinglePage) {
					finalIndex = 0;
					setCurrentImage(finalIndex);
					// Preload neighbours for single-page wrap
					if (!fullImagesRef.current[finalIndex])
						loadFullImage(finalIndex);
					if (
						finalIndex + 1 < totalImagesOnCurrentPage &&
						!fullImagesRef.current[finalIndex + 1]
					)
						loadFullImage(finalIndex + 1);
					if (
						totalImagesOnCurrentPage > 1 &&
						!fullImagesRef.current[totalImagesOnCurrentPage - 1]
					)
						loadFullImage(totalImagesOnCurrentPage - 1);
				} else {
					targetPage = page === pictureList.pageCount ? 1 : page + 1;
					finalIndex = 0; // Target index is always 0 for next page
					if (page !== targetPage) {
						handlePageChange(targetPage);
						setTargetIndexAfterPageLoad(0); // Signal first image
					}
				}
			} else {
				// Simple navigation within the current page
				finalIndex = newIndex;
				setCurrentImage(finalIndex);
				// Preload neighbours
				if (!fullImagesRef.current[finalIndex]) {
					loadFullImage(finalIndex);
				}
				if (
					finalIndex + 1 < totalImagesOnCurrentPage &&
					!fullImagesRef.current[finalIndex + 1]
				) {
					loadFullImage(finalIndex + 1);
				}
				if (
					finalIndex - 1 >= 0 &&
					!fullImagesRef.current[finalIndex - 1]
				) {
					loadFullImage(finalIndex - 1);
				}
				// Preload more images ahead in the direction of travel
				// so rapid clicking doesn't hit unloaded images
				for (let i = 2; i <= 4; i++) {
					const aheadIdx = finalIndex + i;
					if (
						aheadIdx < totalImagesOnCurrentPage &&
						!fullImagesRef.current[aheadIdx]
					) {
						loadFullImage(aheadIdx);
					}
					const behindIdx = finalIndex - i;
					if (behindIdx >= 0 && !fullImagesRef.current[behindIdx]) {
						loadFullImage(behindIdx);
					}
				}
			}
		},
		[
			pictureList,
			loadFullImage,
			page,
			handlePageChange,
			setTargetIndexAfterPageLoad,
		] // Removed fullImages dependency
	);

	const openLightbox = useCallback(
		(index: number) => {
			if (!pictureList) return;

			// Load current and adjacent media items
			loadFullImage(index);
			if (index + 1 < pictureList.mediaItems.length) {
				loadFullImage(index + 1);
			}
			if (index - 1 >= 0) {
				loadFullImage(index - 1);
			}

			// Set current media item and open lightbox
			setCurrentImage(index);
			setIsLightboxOpen(true);
		},
		[pictureList, loadFullImage]
	);

	const closeLightbox = useCallback(() => {
		setCurrentImage(null);
		setIsLightboxOpen(false);
	}, []);

	// Effect for keyboard events
	useEffect(() => {
		if (!isLightboxOpen) return; // Only listen when lightbox is open

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.repeat) return;
			if (e.key === 'ArrowRight') {
				e.preventDefault();
				if (currentImage !== null) {
					changeImage(currentImage + 1);
				}
			} else if (e.key === 'ArrowLeft') {
				e.preventDefault();
				if (currentImage !== null) {
					changeImage(currentImage - 1);
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				closeLightbox();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isLightboxOpen, currentImage, changeImage, closeLightbox]); // Dependencies refined

	// Effect to keep the ref synchronized with the state
	useEffect(() => {
		fullImagesRef.current = fullImages;
	}, [fullImages]);

	// Effect to reset fullImages, set currentImage after page load, and preload
	useEffect(() => {
		if (pictureList) {
			let imageIndexToUse = currentImage;

			// Reset fullImages state when pictureList changes (new page data)
			// Check if the length differs or if the first item's ID differs (heuristic for page change)
			const firstItemId = pictureList.mediaItems[0]?._id;
			const firstRefId = fullImagesRef.current[0]?._id; // Assuming GalleryImage might get _id later
			const shouldReset =
				fullImagesRef.current.length !==
					pictureList.mediaItems.length ||
				(firstItemId && firstRefId && firstItemId !== firstRefId); // Basic check

			if (shouldReset) {
				// console.log('Resetting fullImages due to pictureList change');
				setFullImages(
					new Array(pictureList.mediaItems.length).fill(null)
				);
			}

			// Set currentImage if a page transition just occurred
			if (targetIndexAfterPageLoad !== null) {
				if (targetIndexAfterPageLoad === 0) {
					imageIndexToUse = 0;
					setCurrentImage(0);
				} else if (targetIndexAfterPageLoad === -1) {
					const lastIndex = pictureList.mediaItems.length - 1;
					imageIndexToUse = lastIndex >= 0 ? lastIndex : 0;
					setCurrentImage(imageIndexToUse);
				}
				setTargetIndexAfterPageLoad(null); // Reset the signal
			}

			// Preloading logic using the potentially updated imageIndexToUse
			if (
				isLightboxOpen &&
				imageIndexToUse !== null &&
				imageIndexToUse >= 0 &&
				imageIndexToUse < pictureList.mediaItems.length
			) {
				const index = imageIndexToUse;
				// Use the *updated* ref after potential reset
				const currentFullImages = fullImagesRef.current;

				if (!currentFullImages[index]) {
					loadFullImage(index);
				}
				if (
					index + 1 < pictureList.mediaItems.length &&
					!currentFullImages[index + 1]
				) {
					loadFullImage(index + 1);
				}
				if (index - 1 >= 0 && !currentFullImages[index - 1]) {
					loadFullImage(index - 1);
				}
			}
		}
	}, [
		pictureList,
		isLightboxOpen,
		currentImage, // Still needed as trigger
		loadFullImage,
		targetIndexAfterPageLoad,
		setTargetIndexAfterPageLoad,
	]);

	return {
		isLightboxOpen,
		currentImage,
		fullImages, // Return the state version for rendering
		openLightbox,
		closeLightbox,
		changeImage,
	};
};

export default useLightbox;

// Add _id to GalleryImage if needed for comparison, or adjust reset logic
// interface GalleryImage {
// 	_id?: string; // Optional if added
// 	src: string;
// 	width: number;
// 	height: number;
// 	caption?: string;
// }
